import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { DocumentTextIcon as DocumentTextIconSolid } from '@heroicons/react/24/solid';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import logger from '../../utils/logger';

const Invoices = ({ showNotification }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/invoices');
      if (response.data.success) {
        setInvoices(response.data.data.invoices || []);
      }
    } catch (err) {
      logger.error('Error fetching invoices:', err);
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewInvoice = async (invoice) => {
    try {
      setPdfLoading(true);
      setViewingInvoice(invoice);
      const response = await axiosInstance.get(
        `/api/invoices/${invoice._id}/view`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
    } catch (err) {
      logger.error('Error viewing invoice:', err);
      showNotification?.('Failed to load invoice preview.', 'error');
      setViewingInvoice(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const closeViewer = () => {
    setViewingInvoice(null);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axiosInstance.get(
        `/api/invoices/${invoiceId}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Error downloading invoice:', err);
      showNotification?.('Failed to download invoice. Please try again.', 'error');
    }
  };

  const getStatusConfig = (status, invoiceType) => {
    if (invoiceType === 'credit_note' || status === 'credit') {
      return { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-500', label: 'Credit' };
    }
    const map = {
      paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Paid' },
      pending: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500', label: 'Pending' },
      failed: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', dot: 'bg-red-500', label: 'Failed' },
      refunded: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', dot: 'bg-purple-500', label: 'Refunded' }
    };
    return map[status] || map.pending;
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Parse a more readable description from the raw item description
  const getInvoiceLabel = (invoice) => {
    const desc = invoice.items?.[0]?.description || '';
    if (desc.includes('Billing Plan Change') || desc.includes('billing cycle change')) {
      const isCredit = invoice.invoiceType === 'credit_note' || invoice.status === 'credit';
      if (desc.includes('Monthly to Annual') || desc.includes('monthly') && desc.includes('annual')) {
        return isCredit ? 'Credit — Switched to Annual' : 'Switched to Annual Billing';
      }
      if (desc.includes('Annual to Monthly') || desc.includes('annually') && desc.includes('month')) {
        return isCredit ? 'Credit — Switched to Monthly' : 'Switched to Monthly Billing';
      }
      return isCredit ? 'Billing Plan Credit' : 'Billing Plan Change';
    }
    if (desc.includes('Iris Pro')) return 'Iris Pro License';
    if (desc.includes('Iris Autonomous')) return 'Iris Autonomous';
    if (desc.includes('Email Hosting')) return 'Email Hosting';
    if (desc.includes('ERP')) return 'ERP User Seats';
    if (desc.includes('AI Agent')) return 'AI Agents';
    // Truncate long descriptions
    return desc.length > 40 ? desc.substring(0, 40) + '...' : desc || 'Invoice';
  };

  const isCredit = (invoice) => invoice.invoiceType === 'credit_note' || invoice.status === 'credit';

  // Stats
  const paidInvoices = invoices.filter(inv => inv.status === 'paid' && inv.invoiceType !== 'credit_note');
  const pendingInvoices = invoices.filter(inv => inv.status === 'pending');
  const totalSpent = paidInvoices.reduce((sum, inv) => sum + (inv.total || inv.amountPaid || 0), 0);
  const pendingTotal = pendingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const creditTotal = invoices.filter(inv => isCredit(inv)).reduce((sum, inv) => sum + (inv.total || 0), 0);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/billing')}
            className="p-1 -ml-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors cursor-pointer"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </button>
          <DocumentTextIconSolid className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Invoices & Receipts</h1>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {invoices.length > 0 ? `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}` : 'Payment history'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Loading invoices...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-red-200 dark:border-red-800 p-6">
              <div className="flex items-start gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error loading invoices</h3>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
                </div>
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-[#222222] flex items-center justify-center mx-auto mb-4">
                <DocumentTextIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No invoices yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-6">
                Invoices will appear here after your first payment is processed.
              </p>
              <button
                onClick={() => navigate('/billing')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
              >
                Back to Billing
              </button>
            </div>
          ) : (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
                  <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Invoices</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">{invoices.length}</p>
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
                  <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Unpaid</p>
                  <p className="text-lg font-bold text-amber-600 dark:text-amber-400 mt-0.5">{formatCurrency(pendingTotal)}</p>
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
                  <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Total Paid</p>
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{formatCurrency(totalSpent)}</p>
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3">
                  <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Credits</p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400 mt-0.5">
                    {creditTotal > 0 ? `-${formatCurrency(creditTotal)}` : formatCurrency(0)}
                  </p>
                </div>
              </div>

              {/* Invoice List */}
              <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {invoices.map((invoice) => {
                    const status = getStatusConfig(invoice.status, invoice.invoiceType);
                    const isCreditInvoice = isCredit(invoice);
                    const amount = invoice.total || invoice.amountPaid || invoice.amountDue || 0;
                    const txId = invoice.transactionId || invoice.stripePaymentIntentId;

                    return (
                      <div
                        key={invoice._id}
                        className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/50 dark:hover:bg-[#151515] transition-colors group"
                      >
                        {/* Icon */}
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isCreditInvoice ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-100 dark:bg-[#222222]'}`}>
                          <DocumentTextIcon className={`w-4.5 h-4.5 ${isCreditInvoice ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`} />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {getInvoiceLabel(invoice)}
                            </p>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${status.bg} ${status.text}`}>
                              <span className={`w-1 h-1 rounded-full ${status.dot}`}></span>
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400 dark:text-gray-500">{invoice.invoiceNumber}</span>
                            <span className="text-gray-300 dark:text-gray-700">·</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(invoice.createdAt)}</span>
                            {txId && (
                              <>
                                <span className="text-gray-300 dark:text-gray-700">·</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[140px]">{txId}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="flex-shrink-0 text-right mr-2">
                          <p className={`text-sm font-semibold tabular-nums ${isCreditInvoice ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                            {isCreditInvoice ? '-' : ''}{formatCurrency(amount)}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            onClick={() => viewInvoice(invoice)}
                            title="View invoice"
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => downloadInvoice(invoice._id, invoice.invoiceNumber)}
                            title="Download PDF"
                            className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors cursor-pointer"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={closeViewer}>
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col mx-4" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{viewingInvoice.invoiceNumber}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(viewingInvoice.createdAt)} — {isCredit(viewingInvoice) ? '-' : ''}{formatCurrency(viewingInvoice.total)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => downloadInvoice(viewingInvoice._id, viewingInvoice.invoiceNumber)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors cursor-pointer"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Download
                </button>
                <button
                  onClick={closeViewer}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PDF Content */}
            <div className="flex-1 min-h-0 bg-gray-100 dark:bg-[#111111] rounded-b-xl overflow-hidden">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading invoice...</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title={`Invoice ${viewingInvoice.invoiceNumber}`}
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Failed to load invoice preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
